import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { dashboardAPI } from "../services/api";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (err) {
      setError("Failed to load dashboard data");
      console.error("Dashboard error:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCompactCurrency = (amount) => {
    const num = parseFloat(amount);
    if (num >= 1000000) {
      return `KSh ${(num / 1000000).toFixed(0)}M`;
    } else if (num >= 1000) {
      return `KSh ${(num / 1000).toFixed(0)}K`;
    }
    return formatCurrency(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="animate-pulse px-4 sm:px-6 lg:px-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card">
              <div className="card-body p-4">
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <div className="text-red-600 mb-4 text-sm sm:text-base">{error}</div>
        <button onClick={fetchDashboardStats} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444"];

  return (
    <div className="space-y-4 sm:space-y-6 px-4 sm:px-6 lg:px-0">
      {/* Page Header */}
      <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
            Dashboard
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-gray-600">
            Overview of your customer management activities
          </p>
        </div>
        <div className="w-full sm:w-auto">
          <Link to="/customers" className="btn btn-primary w-full sm:w-auto justify-center">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            <span className="text-sm sm:text-base">Add Customer</span>
          </Link>
        </div>
      </div>

      {/* Stats Cards with Gradient Backgrounds */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        <div className="card bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg hover:shadow-xl transition-shadow">
          <div className="card-body p-4 sm:p-5">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm flex-shrink-0">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-blue-100 truncate">
                  Total Customers
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-white">
                  {stats?.total_customers || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg hover:shadow-xl transition-shadow">
          <div className="card-body p-4 sm:p-5">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm flex-shrink-0">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-green-100 truncate">
                  Interactions
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-white">
                  {stats?.interactions_this_month || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-lg hover:shadow-xl transition-shadow">
          <div className="card-body p-4 sm:p-5">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm flex-shrink-0">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                  />
                </svg>
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-orange-100 truncate">
                  Orders
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-white">
                  {stats?.orders_this_month || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg hover:shadow-xl transition-shadow">
          <div className="card-body p-4 sm:p-5">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm flex-shrink-0">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                  />
                </svg>
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-purple-100 truncate">
                  Revenue
                </p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-white break-words">
                  {formatCompactCurrency(stats?.revenue_this_month || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alert Cards with Gradient Accent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="card border-l-4 border-l-warning-400 bg-gradient-to-r from-orange-50 to-white">
          <div className="card-body p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  Follow-up Required
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Customers not contacted in 14+ days
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-warning-600 mt-2">
                  {stats?.customers_need_followup || 0}
                </p>
              </div>
              <Link
                to="/customers?filter=no_contact"
                className="btn btn-secondary w-full sm:w-auto text-sm"
              >
                View All
              </Link>
            </div>
          </div>
        </div>

        <div className="card border-l-4 border-l-danger-400 bg-gradient-to-r from-red-50 to-white">
          <div className="card-body p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  No Recent Orders
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Customers without orders in 30+ days
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-danger-600 mt-2">
                  {stats?.customers_no_recent_orders || 0}
                </p>
              </div>
              <Link
                to="/customers?filter=no_orders"
                className="btn btn-secondary w-full sm:w-auto text-sm"
              >
                View All
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section with Enhanced Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Interaction Trends */}
        <div className="card bg-gradient-to-br from-blue-50 to-white shadow-md">
          <div className="card-header p-4 sm:p-5">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Interaction Trends
            </h3>
            <p className="text-xs sm:text-sm text-gray-600">Last 6 months</p>
          </div>
          <div className="card-body p-4 sm:p-5">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={stats?.interaction_trends || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#3b82f6"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Contact Method Distribution */}
        <div className="card bg-gradient-to-br from-green-50 to-white shadow-md">
          <div className="card-header p-4 sm:p-5">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Contact Methods
            </h3>
            <p className="text-xs sm:text-sm text-gray-600">Last 30 days</p>
          </div>
          <div className="card-body p-4 sm:p-5">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={stats?.contact_method_distribution || []}
                  cx="50%"
                  cy="50%"
                  outerRadius={window.innerWidth < 640 ? 60 : 80}
                  fill="#8884d8"
                  dataKey="count"
                  label={({ contact_method, percent }) =>
                    window.innerWidth < 640 
                      ? `${(percent * 100).toFixed(0)}%`
                      : `${contact_method} ${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={window.innerWidth >= 640}
                >
                  {(stats?.contact_method_distribution || []).map(
                    (entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    )
                  )}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Activities & Top Customers with Gradient */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent Activities */}
        <div className="card bg-gradient-to-br from-indigo-50 to-white shadow-md">
          <div className="card-header p-4 sm:p-5 flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Recent Activities
            </h3>
            <Link
              to="/interactions"
              className="text-xs sm:text-sm text-primary-600 hover:text-primary-800"
            >
              View all
            </Link>
          </div>
          <div className="card-body p-0">
            <div className="divide-y divide-gray-200">
              {(stats?.recent_activities || []).map((activity, index) => (
                <div key={index} className="p-3 sm:p-4 hover:bg-white transition-colors">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div
                        className={`w-2 h-2 rounded-full mt-2 ${
                          activity.contact_method === "Visited"
                            ? "bg-success-500"
                            : activity.contact_method === "Phone Call"
                            ? "bg-primary-500"
                            : "bg-warning-500"
                        }`}
                      ></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                          {activity.customer_name}
                        </p>
                        <p className="text-xs text-gray-500 ml-2 flex-shrink-0">
                          {formatDate(activity.contact_date)}
                        </p>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">
                        {activity.contact_method} -{" "}
                        {activity.notes || "No notes"}
                      </p>
                      <p className="text-xs text-gray-500">
                        by {activity.created_by_name}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {(!stats?.recent_activities ||
                stats.recent_activities.length === 0) && (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No recent activities
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Top Customers */}
        <div className="card bg-gradient-to-br from-emerald-50 to-white shadow-md">
          <div className="card-header p-4 sm:p-5 flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Top Customers
            </h3>
            <Link
              to="/customers"
              className="text-xs sm:text-sm text-primary-600 hover:text-primary-800"
            >
              View all
            </Link>
          </div>
          <div className="card-body p-0">
            <div className="divide-y divide-gray-200">
              {(stats?.top_customers || []).map((customer, index) => (
                <div key={index} className="p-3 sm:p-4 hover:bg-white transition-colors">
                  <div className="flex items-center justify-between space-x-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                        {customer.customer_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {customer.contact_number}
                      </p>
                      <p className="text-xs text-gray-500">
                        {customer.order_count} orders • Last:{" "}
                        {formatDate(customer.last_order_date)}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs sm:text-sm font-semibold text-success-600 whitespace-nowrap">
                        {formatCompactCurrency(customer.total_orders)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {(!stats?.top_customers || stats.top_customers.length === 0) && (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No customer data available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;